import React from 'react';
import { createUseStyles } from 'react-jss';

import { createURL } from '@sm/utils';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { IconChevronLeft } from '@wds/icons';
import { t, T } from '@sm/intl';
import { List } from '@wds/list';
import { Grid } from '@wds/grid';
import { Theme } from '@wds/styles';
import { generateMetricsAttribute } from '@sm/metrics';

import audienceImage from './images/audience.svg';
import copyPasteImage from './images/copy-paste.svg';
import createFromScratchImage from './images/create-scratch.svg';
import guidanceImage from './images/bifm.svg';
import guidedSetupImage from './images/guided-setup.svg';
import solutionImage from './images/solution.svg';
import templateImage from './images/template.svg';
import {
  ClickMenuItem,
  ClickMetricEventName,
  HrefMenuItem,
  isClickMenuItem,
  isHrefMenuItem,
  View,
  ViewListItem,
} from './types';

export const COPY = {
  startingPoint: {
    id: 'ButtonCreateVariant.startingPoint',
    defaultMessage: 'Choose a starting point...',
    description:
      '[Type: Header][Vis.: high] - Heading for sidebar drawer menu for creating a survey',
  },
  guidedSetup: {
    id: 'ButtonCreateVariant.guidedSetup',
    defaultMessage: 'Guided setup',
    description:
      '[Type: Header][Vis.: high] - Heading for sidebar drawer menu for using our solutions products',
  },
  goBackToStartingPoint: {
    id: 'ButtonCreateVariant.goBackToStartingPoint',
    defaultMessage: 'Go back to "Choose a starting point"',
    description:
      '[Type: Label][Vis.: high] - Label used exclusively for screen readers for going back in a menu',
  },
  startWithBlank: {
    id: 'ButtonCreateVariant.startWithBlank',
    defaultMessage: 'Start with a blank survey',
    description:
      '[Type: Label][Vis.: high] - Title for a link to survey creation, starting from scratch',
  },
  startWithBlankDesc: {
    id: 'ButtonCreateVariant.startWithBlankDesc',
    defaultMessage: 'Best for experienced survey authors',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to survey creation, starting from scratch',
  },
  startWithBlankVariantDesc: {
    id: 'ButtonCreateVariant.startWithBlankVariantDesc',
    defaultMessage:
      'Good for those who want to start from scratch or copy and paste',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to survey creation, starting from scratch -- variant copy',
  },
  copyPaste: {
    id: 'ButtonCreateVariant.copyPaste',
    defaultMessage: 'Copy and paste from doc',
    description:
      '[Type: Label][Vis.: high] - Title for a link to survey creation, starting with copy/paste',
  },
  copyPasteDesc: {
    id: 'ButtonCreateVariant.copyPasteDesc',
    defaultMessage: 'Great for anyone who wants to import prewritten questions',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to survey creation, starting with copy/paste',
  },
  guidedSetupMenuItem: {
    id: 'ButtonCreateVariant.guidedSetupMenuItem',
    defaultMessage: 'Try guided setup',
    description:
      '[Type: Label][Vis.: high] - Title for a button that opens a sub-menu detailing our marketing solutions',
  },
  guidedSetupMenuItemDesc: {
    id: 'ButtonCreateVariant.guidedSetupMenuItemDesc',
    defaultMessage:
      'Helpful for those who want guidance, templates or brand/marketing insights',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a button that opens a sub-menu detailing our marketing solutions',
  },
  guidedSetupVariantDesc: {
    id: 'ButtonCreateVariant.guidedSetupVariantDesc',
    defaultMessage: 'Helpful for people who like step-by-step guidance',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a button that opens create with "build it for me" auto-opened',
  },
  buyResponses: {
    id: 'ButtonCreateVariant.buyResponses',
    defaultMessage: 'Buy targeted responses',
    description:
      '[Type: Label][Vis.: high] - Title for a link to Audience preview',
  },
  buyResponsesDesc: {
    id: 'ButtonCreateVariant.buyResponsesDesc',
    defaultMessage:
      'Excellent for people who want to start by choosing audience demographics',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  insightsSolutions: {
    id: 'ButtonCreateVariant.insightsSolutions',
    defaultMessage: 'Insights solutions',
    description:
      '[Type: Label][Vis.: high] - Title for a link to Audience preview',
  },
  insightsSolutionsVariant: {
    id: 'ButtonCreateVariant.insightsSolutionsVariant',
    defaultMessage: 'Do brand or market research',
    description:
      '[Type: Label][Vis.: high] - Title for a link to the solutions marketplace launchpad',
  },
  insightsSolutionsVariantDesc: {
    id: 'ButtonCreateVariant.insightsSolutionsVariantDesc',
    defaultMessage:
      'Useful for those who want to test an idea or track a brand/market',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a button that links to the solutions marketplace launchpad',
  },
  insightsSolutionsDesc: {
    id: 'ButtonCreateVariant.insightsSolutionsDesc',
    defaultMessage: 'Get an AI-backed solution like...',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  brandTracking: {
    id: 'ButtonCreateVariant.brandTracking',
    defaultMessage: 'Brand tracking',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  conceptTesting: {
    id: 'ButtonCreateVariant.conceptTesting',
    defaultMessage: 'Concept testing',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  ideaScreening: {
    id: 'ButtonCreateVariant.ideaScreening',
    defaultMessage: 'Idea screening',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  muchMore: {
    id: 'ButtonCreateVariant.muchMore',
    defaultMessage: 'Much more',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  productGuidance: {
    id: 'ButtonCreateVariant.productGuidance',
    defaultMessage: 'In-product guidance',
    description:
      '[Type: Label][Vis.: high] - Title for a link to Audience preview',
  },
  productGuidanceDesc: {
    id: 'ButtonCreateVariant.productGuidanceDesc',
    defaultMessage: 'Let SurveyMonkey help via...',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  stepByStep: {
    id: 'ButtonCreateVariant.stepByStep',
    defaultMessage: 'Step-by-step instructions',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  standardSurveys: {
    id: 'ButtonCreateVariant.standardSurveys',
    defaultMessage: 'Standard surveys',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  featureExplanations: {
    id: 'ButtonCreateVariant.featureExplanations',
    defaultMessage: 'Feature explanations',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  customizationTips: {
    id: 'ButtonCreateVariant.customizationTips',
    defaultMessage: 'Customization tips',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  popularTemplate: {
    id: 'ButtonCreateVariant.popularTemplate',
    defaultMessage: 'Popular template',
    description:
      '[Type: Label][Vis.: high] - Title for a link to Audience preview',
  },
  popularTemplateDesc: {
    id: 'ButtonCreateVariant.popularTemplateDesc',
    defaultMessage: 'Pick from templates like...',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  popularTemplateVariant: {
    id: 'ButtonCreateVariant.popularTemplateVariant',
    defaultMessage: 'Pick a popular template',
    description:
      '[Type: Label][Vis.: high] - Title for a link to survey creation with templates auto-opened',
  },
  popularTemplateVariantDesc: {
    id: 'ButtonCreateVariant.popularTemplateVariantDesc',
    defaultMessage: 'Good for anyone who wants to ask common questions',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to survey creation with templates auto-opened',
  },
  customerSatisfaction: {
    id: 'ButtonCreateVariant.customerSatisfaction',
    defaultMessage: 'Customer satisfaction',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  employeeEngagement: {
    id: 'ButtonCreateVariant.employeeEngagement',
    defaultMessage: 'Employee engagement',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  netPromoterScore: {
    id: 'ButtonCreateVariant.netPromoterScore',
    defaultMessage: 'Net promoter score',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
  manyOthers: {
    id: 'ButtonCreateVariant.manyOthers',
    defaultMessage: 'Many others',
    description:
      '[Type: Paragraph][Vis.: high] - Description for a link to Audience preview',
  },
};

const convertToMenuItem = (
  viewListItem: ViewListItem,
  clickHandler?: () => void
): HrefMenuItem | ClickMenuItem => {
  if (!viewListItem.navigationTarget && clickHandler) {
    return { ...viewListItem, onClick: clickHandler } as ClickMenuItem;
  }
  return {
    ...viewListItem,
    href: viewListItem.navigationTarget ?? '',
  } as HrefMenuItem;
};

const startFromScratch = {
  eventName: ClickMetricEventName.StartFromScratch,
  title: t(COPY.startWithBlank),
  iconSrc: createFromScratchImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'scratch',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.startWithBlankDesc} />
    </Typography>
  ),
};

const startFromScratchVariant = {
  eventName: ClickMetricEventName.StartFromScratch,
  title: t(COPY.startWithBlank),
  iconSrc: createFromScratchImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'scratch',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.startWithBlankVariantDesc} />
    </Typography>
  ),
};

const copyAndPaste = {
  eventName: ClickMetricEventName.CopyAndPaste,
  title: t(COPY.copyPaste),
  iconSrc: copyPasteImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'importQuestions',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.copyPasteDesc} />
    </Typography>
  ),
};

const seeGuidedSetupOptions = {
  title: t(COPY.guidedSetupMenuItem),
  iconSrc: guidedSetupImage,
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.guidedSetupMenuItemDesc} />
    </Typography>
  ),
};

const buyResponses = {
  eventName: ClickMetricEventName.Audience,
  title: t(COPY.buyResponses),
  iconSrc: audienceImage,
  navigationTarget: createURL('/collect/audience/calculator/preview/', {
    ut_source: 'header',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.buyResponsesDesc} />
    </Typography>
  ),
};

const solutions = {
  eventName: ClickMetricEventName.Solution,
  title: t(COPY.insightsSolutions),
  iconSrc: solutionImage,
  navigationTarget: createURL('/market-research/solutions/get-started/', {
    ut_source: 'header',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.insightsSolutionsDesc} />
      <ul>
        <li>
          <T desc={COPY.brandTracking} />
        </li>
        <li>
          <T desc={COPY.conceptTesting} />
        </li>
        <li>
          <T desc={COPY.ideaScreening} />
        </li>
        <li>
          <T desc={COPY.muchMore} />
        </li>
      </ul>
    </Typography>
  ),
};

const solutionsVariant = {
  eventName: ClickMetricEventName.Solution,
  title: t(COPY.insightsSolutionsVariant),
  iconSrc: solutionImage,
  navigationTarget: createURL('/market-research/solutions/get-started/', {
    ut_source: 'header',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.insightsSolutionsVariantDesc} />
    </Typography>
  ),
};

const buildItForMe = {
  eventName: ClickMetricEventName.BuildItForMe,
  title: t(COPY.productGuidance),
  iconSrc: guidanceImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'bifm',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.productGuidanceDesc} />
      <ul>
        <li>
          <T desc={COPY.stepByStep} />
        </li>
        <li>
          <T desc={COPY.standardSurveys} />
        </li>
        <li>
          <T desc={COPY.featureExplanations} />
        </li>
        <li>
          <T desc={COPY.customizationTips} />
        </li>
      </ul>
    </Typography>
  ),
};

const buildItForMeVariant = {
  eventName: ClickMetricEventName.BuildItForMe,
  title: t(COPY.guidedSetupMenuItem),
  iconSrc: guidanceImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'bifm',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.guidedSetupVariantDesc} />
    </Typography>
  ),
};

const templates = {
  eventName: ClickMetricEventName.Template,
  title: t(COPY.popularTemplate),
  iconSrc: templateImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'template',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.popularTemplateDesc} />
      <ul>
        <li>
          <T desc={COPY.customerSatisfaction} />
        </li>
        <li>
          <T desc={COPY.employeeEngagement} />
        </li>
        <li>
          <T desc={COPY.netPromoterScore} />
        </li>
        <li>
          <T desc={COPY.manyOthers} />
        </li>
      </ul>
    </Typography>
  ),
};

const templatesVariant = {
  eventName: ClickMetricEventName.Template,
  title: t(COPY.popularTemplateVariant),
  iconSrc: templateImage,
  navigationTarget: createURL('/create/', {
    ut_source: 'header',
    create: 'template',
  }),
  bodyContent: (
    <Typography variant="bodySm">
      <T desc={COPY.popularTemplateVariantDesc} />
    </Typography>
  ),
};

const useStyles = createUseStyles((theme: Theme) => ({
  listItemContent: {
    whiteSpace: 'break-spaces',
    marginTop: `${theme.spacing[1]}px`,
  },
  sheetTitle: {
    margin: `${theme.spacing[5]}px 0 ${theme.spacing[5]}px ${theme.spacing[4]}px`,
  },
  listItem: {
    padding: `${theme.spacing[4]}px 0 ${theme.spacing[4]}px 0`,
  },
  listItemTitle: {
    fontSize: '16px',
  },
  backButton: {
    textDecoration: 'none',
    border: 'none',
    background: 'transparent',
    '&:hover': {
      cursor: 'pointer',
      background: theme.palette.background.accent,
    },
  },
  backButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: `${theme.spacing[2]}px`,
    color: theme.palette.text.darkMuted,
    fontSize: '20px',
  },
  iconImageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
    '& img': {
      height: '48px',
      width: '48px',
    },
  },
}));

const ListItem = ({
  listItem,
  pageName,
}: {
  listItem: ClickMenuItem | HrefMenuItem;
  pageName: string;
}): JSX.Element => {
  const {
    listItem: listItemClass,
    listItemContent,
    listItemTitle,
    iconImageContainer,
  } = useStyles();

  return (
    <List.Item
      navigationTarget={isHrefMenuItem(listItem) ? listItem.href : undefined}
      isNavigable={isHrefMenuItem(listItem)}
      onClick={isClickMenuItem(listItem) ? listItem.onClick : undefined}
      data-sm-metrics={
        isHrefMenuItem(listItem) && listItem.eventName
          ? generateMetricsAttribute({
              data: {
                pageName,
                amplitudeEvent: 'create menu clicked option',
                ctaName: listItem.eventName,
              },
            })
          : undefined
      }
      interactive
    >
      <div className={listItemClass}>
        <Grid noBleeds noGutters fluid>
          <Grid.Item xs={2}>
            <div className={iconImageContainer}>
              <img src={listItem.iconSrc} alt="" />
            </div>
          </Grid.Item>
          <Grid.Item xs={10}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <strong className={listItemTitle}>
                <strong>{listItem.title}</strong>
              </strong>
              <div className={listItemContent}>{listItem.bodyContent}</div>
            </Box>
          </Grid.Item>
        </Grid>
      </div>
    </List.Item>
  );
};

export const DrillDownMenuVariant = ({
  view,
  setView,
  pageName,
}: {
  pageName: string;
  view: View;
  setView(v: View): void;
}): JSX.Element => {
  const { sheetTitle, backButton, backButtonIcon } = useStyles();

  const startingPointMenuItems = [
    startFromScratch,
    copyAndPaste,
    seeGuidedSetupOptions,
    buyResponses,
  ].map(viewListItem =>
    convertToMenuItem(viewListItem, () => setView(View.GuidedSetup))
  );
  const guidedSetupMenuItems = [
    solutions,
    buildItForMe,
    templates,
  ].map(viewListItem => convertToMenuItem(viewListItem));

  return (
    <Choose>
      <When condition={view === View.StartingPoint}>
        <div className={sheetTitle}>
          <Typography variant="sectionTitle">
            <T desc={COPY.startingPoint} />
          </Typography>
        </div>
        <List interactive>
          {startingPointMenuItems.map(viewListItem => (
            <ListItem
              listItem={viewListItem}
              pageName={pageName}
              key={`starting-point-list-item-${viewListItem.title}`}
            />
          ))}
        </List>
      </When>
      <When condition={view === View.GuidedSetup}>
        <button
          type="button"
          className={backButton}
          onClick={() => setView(View.StartingPoint)}
          aria-label={t(COPY.goBackToStartingPoint)}
        >
          <Typography variant="sectionTitle" color="dark">
            <Box display="flex" alignItems="center" className={sheetTitle}>
              <span className={backButtonIcon}>
                <IconChevronLeft title="" />
              </span>
              <T desc={COPY.guidedSetup} />
            </Box>
          </Typography>
        </button>
        <List interactive>
          {guidedSetupMenuItems.map(viewListItem => (
            <ListItem
              listItem={viewListItem}
              pageName={pageName}
              key={`guided-setup-list-item-${viewListItem.title}`}
            />
          ))}
        </List>
      </When>
    </Choose>
  );
};

export const SingleLevelVariant = ({
  view,
  pageName,
}: {
  view: View;
  pageName: string;
}): JSX.Element => {
  const menuItems = [
    startFromScratchVariant,
    templatesVariant,
    buildItForMeVariant,
    solutionsVariant,
    buyResponses,
  ].map(viewListItem => convertToMenuItem(viewListItem));
  const { sheetTitle } = useStyles();

  return (
    <>
      <div className={sheetTitle}>
        <Typography variant="sectionTitle">
          <T desc={COPY.startingPoint} />
        </Typography>
      </div>
      <List interactive>
        {menuItems.map(viewListItem => (
          <ListItem
            listItem={viewListItem}
            pageName={pageName}
            key={`single-level-list-item-${viewListItem.title}`}
          />
        ))}
      </List>
    </>
  );
};
